import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2, RotateCcw, HelpCircle, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useDashboard } from "@/contexts/DashboardContext";

const NOTES = [
    { value: 0, label: "Oubli total", subtitle: "je ne me souviens pas" },
    { value: 1, label: "Incorrect", subtitle: "je me suis trompé·e" },
    { value: 2, label: "Avec aide", subtitle: "j'ai eu besoin d'un indice fort" },
    { value: 3, label: "Hésitant", subtitle: "j'ai douté" },
    { value: 4, label: "Correct", subtitle: "je savais" },
    { value: 5, label: "Naturel", subtitle: "rapide, facile" },
];

export default function Reviews() {
    const [words, setWords] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [idx, setIdx] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [done, setDone] = useState(false);
    const [session, setSession] = useState({ notes: [], startedAt: Date.now() });
    const { refresh: refreshDashboard } = useDashboard();

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/reviews/due");
            setWords(data.words || []);
            setTotalCount(Number(data.count || 0));
            setIdx(0);
            setFlipped(false);
            setDone(false);
            setSession({ notes: [], startedAt: Date.now() });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    const current = words[idx];
    const stats = useMemo(() => {
        const n = session.notes;
        return {
            total: n.length,
            good: n.filter((v) => v >= 4).length,
            fragile: n.filter((v) => v <= 2).length,
        };
    }, [session.notes]);

    async function grade(noteValue) {
        if (!current) return;
        try {
            const { data } = await api.post("/reviews/answer", {
                word_id: current.word_id,
                note: noteValue,
            });
            const newNotes = [...session.notes, noteValue];
            setSession({ ...session, notes: newNotes });
            const next = idx + 1;
            if (next >= words.length) {
                setDone(true);
                refreshDashboard?.();
            } else {
                setIdx(next);
                setFlipped(false);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="reviews-page">
            <header className="mb-10">
                <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Révisions</p>
                <h1
                    className="font-serif text-3xl md:text-4xl tracking-tight"
                    data-testid="reviews-title"
                >
                    {totalCount === 0
                        ? "Rien à réviser aujourd'hui — bravo."
                        : done
                          ? "Session terminée."
                          : `${totalCount - idx} mot${totalCount - idx > 1 ? "s" : ""} à revoir`}
                </h1>
                {!done && words.length > 0 && (
                    <p className="mt-2 text-sm text-inkSoft" data-testid="reviews-progress">
                        Question {idx + 1} / {words.length}
                        {totalCount > words.length ? ` · ${totalCount} au total` : ""} · {stats.good} correct(s),{" "}
                        {stats.fragile} fragile(s)
                    </p>
                )}
            </header>

            {done && (
                <div className="bg-emerald text-white rounded-2xl p-8" data-testid="reviews-done">
                    <Sparkles className="w-6 h-6 mb-3" />
                    <h2 className="font-serif text-2xl leading-tight">
                        Session terminée : {stats.good}/{stats.total} bien retenus.
                    </h2>
                    <p className="text-sm text-white/80 mt-2">
                        Les mots fragiles reviendront très vite ; les autres, plus tard. À demain.
                    </p>
                    <button
                        onClick={load}
                        data-testid="reviews-restart"
                        className="mt-6 inline-flex items-center gap-2 bg-white text-emerald hover:bg-paperAlt rounded-full px-5 py-2 text-sm font-medium active:scale-[0.98] transition-transform"
                    >
                        <RotateCcw className="w-4 h-4" /> Recharger la file
                    </button>
                </div>
            )}

            {words.length === 0 && !done && (
                <div className="bg-white border border-lexiBorder rounded-2xl p-8" data-testid="reviews-empty">
                    <p className="text-sm text-inkSoft">
                        Aucun mot à réviser maintenant. Continuez avec la leçon du jour, ou revenez plus tard.
                    </p>
                </div>
            )}

            {!done && current && (
                <div className="bg-white border border-lexiBorder rounded-2xl p-8 md:p-10 animate-fade-up" data-testid="reviews-card">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-ink mb-3 font-medium">
                        {current.state} · {current.category}
                    </p>
                    <h2
                        className="font-serif text-4xl md:text-5xl leading-tight"
                        data-testid="review-word"
                    >
                        {current.word}
                    </h2>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-inkSoft">
                        <span className="font-mono text-emerald">{current.ipa}</span>
                        <span className="italic">{current.simplified}</span>
                    </div>

                    {!flipped ? (
                        <button
                            onClick={() => setFlipped(true)}
                            data-testid="review-reveal"
                            className="mt-8 inline-flex items-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-5 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                        >
                            <HelpCircle className="w-4 h-4" /> Révéler la réponse
                        </button>
                    ) : (
                        <div className="mt-6 space-y-4 animate-fade-up">
                            <p
                                className="font-serif text-2xl"
                                data-testid="review-translation"
                            >
                                {current.translation_fr}
                            </p>
                            {current.natural_sentence && (
                                <div className="bg-paper border border-lexiBorder rounded-xl p-4">
                                    <p className="font-serif italic">« {current.natural_sentence} »</p>
                                    <p className="text-xs text-inkSoft mt-1">{current.sentence_translation}</p>
                                </div>
                            )}

                            <div>
                                <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">
                                    Notez-vous
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="review-notes">
                                    {NOTES.map((n) => (
                                        <button
                                            key={n.value}
                                            onClick={() => grade(n.value)}
                                            data-testid={`review-note-${n.value}`}
                                            className="text-left p-3 rounded-xl border border-lexiBorder bg-paper hover:bg-paperAlt transition-colors"
                                        >
                                            <div className="font-mono text-sm">{n.value}</div>
                                            <div className="font-medium text-sm mt-0.5">{n.label}</div>
                                            <div className="text-[10px] text-inkSoft">{n.subtitle}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
